/** @typedef {"TE_SEQUENCES"|"TE_ONCE"|"TE_ROUNDS"} TeExecutionType */

export const TE_EXECUTION_TYPES = Object.freeze({
  SEQUENCES: "TE_SEQUENCES",
  ONCE: "TE_ONCE",
  ROUNDS: "TE_ROUNDS",
});

function kotTypeNameMatches(ex) {
  const candidates = [
    ex?.type_ex?.type_name_en,
    ex?.type_ex?.type_name_fr,
    ex?.sub_type_ex?.sub_type_name_en,
    ex?.sub_type_ex_direct?.sub_type_name_en,
    ex?.tbl_exercise?.tbl_type_ex?.type_name_en,
    ex?.tbl_exercise?.tbl_type_ex?.type_name_fr,
  ]
    .filter(Boolean)
    .map((s) => String(s).toLowerCase());
  return candidates.some(
    (s) =>
      (s.includes("king") && s.includes("table")) ||
      s.includes("montée de table") ||
      s.includes("montee de table")
  );
}

/** Catalog type row (tbl_type_ex) — King of the Table / Montée de table. */
export function isKingOfTableExerciseType(typeRow) {
  if (!typeRow) return false;
  const en = String(typeRow.type_name_en || "").trim().toLowerCase();
  const fr = String(typeRow.type_name_fr || "").trim().toLowerCase();
  return (
    en === "king of the table" ||
    fr === "montée de table" ||
    fr === "montee de table"
  );
}

function typeNameBlob(typeRow) {
  if (!typeRow) return "";
  return `${typeRow.type_name_en || ""} ${typeRow.type_name_fr || ""} ${typeRow.type_name_de || ""} ${typeRow.type_name_es || ""}`
    .trim()
    .toLowerCase();
}

/** Catalog type row (tbl_type_ex) — Personalized / Personnalisé. */
export function isPersonalizedExerciseType(typeRow) {
  if (!typeRow) return false;
  const names = [
    typeRow.type_name_en,
    typeRow.type_name_fr,
    typeRow.type_name,
  ];
  return names.some((name) => {
    const n = String(name || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toLowerCase();
    return n === "personalized" || n === "personnalise";
  });
}

const MUTUAL_WARMUP_SUBTYPES = new Set([
  "forehand",
  "backhand",
  "coup droit",
  "revers",
  "vorhand",
  "ruckhand",
  "derecha",
  "reves",
]);

/** Plain forehand / backhand. Topspin strokes alternate performer and sparring. */
export function isMutualWarmupSubtypeName(name) {
  const n = String(name || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
  if (!n) return false;
  if (n.includes("topspin") || n.includes("top spin") || n.includes("top-spin")) return false;
  return MUTUAL_WARMUP_SUBTYPES.has(n);
}

/** Warmup forehand and backhand: both players perform in one sequence. */
export function bothPlayersPerformSubtype(typeRow, subTypeName) {
  return isWarmupExerciseType(typeRow) && isMutualWarmupSubtypeName(subTypeName);
}

/** Catalog type row (tbl_type_ex) — Blocks of competency. */
export function isCompetencyBlockExerciseType(typeRow) {
  if (!typeRow) return false;
  const n = String(typeRow.type_name_en || "").trim().toLowerCase();
  const blob = typeNameBlob(typeRow);
  return (n.includes("block") && n.includes("competency")) || blob.includes("competenc");
}

/** Catalog type row (tbl_type_ex) — Warmup / Échauffement. */
export function isWarmupExerciseType(typeRow) {
  if (!typeRow) return false;
  const en = String(typeRow.type_name_en || "").trim().toLowerCase();
  const fr = String(typeRow.type_name_fr || "").trim().toLowerCase();
  return (
    en === "warmup" ||
    en.includes("warm-up") ||
    fr === "échauffement" ||
    fr === "echauffement"
  );
}

/** Catalog type row (tbl_type_ex) — Service / Aufschlag / Servicio. */
export function isServiceExerciseType(typeRow) {
  if (!typeRow) return false;
  const en = String(typeRow.type_name_en || "").trim().toLowerCase();
  const blob = typeNameBlob(typeRow);
  return (
    en === "service" ||
    blob.split(/\s+/).includes("service") ||
    blob.includes("aufschlag") ||
    blob.includes("servicio")
  );
}

export function isServiceExerciseTypeName(typeNameEn) {
  const n = String(typeNameEn || "").trim().toLowerCase();
  if (!n) return false;
  return (
    n === "service" ||
    n === "aufschlag" ||
    n === "servicio" ||
    n === "servizio"
  );
}

/** Live training / session row that is a Service drill. */
export function isServiceExercise(ex) {
  if (!ex) return false;
  if (isServiceExerciseTypeName(ex.type_ex?.type_name_en)) return true;
  if (isServiceExerciseTypeName(ex.tbl_type_ex?.type_name_en)) return true;
  if (isServiceExerciseTypeName(ex.exercise?.type_ex?.type_name_en)) return true;
  if (isServiceExerciseTypeName(ex.exercise?.tbl_type_ex?.type_name_en)) return true;
  if (isServiceExerciseTypeName(ex.tbl_exercise?.tbl_type_ex?.type_name_en)) return true;
  return (
    isServiceExerciseType(ex.type_ex) ||
    isServiceExerciseType(ex.tbl_type_ex) ||
    isServiceExerciseType(ex.exercise?.type_ex) ||
    isServiceExerciseType(ex.exercise?.tbl_type_ex) ||
    isServiceExerciseType(ex.tbl_exercise?.tbl_type_ex) ||
    isServiceExerciseType(ex.sub_type_ex?.tbl_type_ex) ||
    isServiceExerciseType(ex.sub_type_ex_direct?.tbl_type_ex)
  );
}

export function isMultiballExerciseTypeName(typeName) {
  const n = String(typeName || "").trim().toLowerCase();
  if (!n) return false;
  return (
    n === "multiball" ||
    n === "multiballe" ||
    n === "multiballes" ||
    n === "multibola" ||
    n.includes("multi-ball") ||
    n.includes("multi ball")
  );
}

/** Live training / session row that is a Multiball drill (one side performs, the other feeds). */
export function isMultiballExercise(ex) {
  if (!ex) return false;
  return exerciseTypeNameCandidates(ex).some((n) => isMultiballExerciseTypeName(n));
}

/**
 * Service and multiball: one side performs at a time and the partner is
 * optional, so a single seated player can run the block.
 */
export function isSoloCapableSequenceExercise(ex) {
  return isServiceExercise(ex) || isMultiballExercise(ex);
}

/** Live training / session row that is a warmup (Échauffement). */
export function isWarmupExercise(ex) {
  if (!ex) return false;
  return (
    isWarmupExerciseType(ex.type_ex) ||
    isWarmupExerciseType(ex.tbl_type_ex) ||
    isWarmupExerciseType(ex.exercise?.type_ex) ||
    isWarmupExerciseType(ex.exercise?.tbl_type_ex) ||
    isWarmupExerciseType(ex.tbl_exercise?.tbl_type_ex)
  );
}

/** Catalog type row (tbl_type_ex) — Off-table / Exercices hors table. */
export function isOffTableExerciseType(typeRow) {
  if (!typeRow) return false;
  const en = String(typeRow.type_name_en || "").trim().toLowerCase();
  const fr = String(typeRow.type_name_fr || "").trim().toLowerCase();
  const blob = `${en} ${fr}`;
  return (
    en === "off-table exercises" ||
    blob.includes("off-table") ||
    blob.includes("off table") ||
    blob.includes("hors table")
  );
}

export function isOffTableExerciseTypeName(typeNameEn) {
  const n = String(typeNameEn || "").trim().toLowerCase();
  if (!n) return false;
  return (
    n === "off-table exercises" ||
    n.includes("off-table") ||
    n.includes("off table") ||
    n.includes("hors table")
  );
}

/** Live training / session row that is an Off-table (out-of-table) exercise. */
export function isOffTableExercise(ex) {
  if (!ex) return false;
  if (isKotExercise(ex)) return false;
  if (exerciseTypeNameCandidates(ex).some((n) => isOffTableExerciseTypeName(n))) {
    return true;
  }
  return (
    isOffTableExerciseType(ex.type_ex) ||
    isOffTableExerciseType(ex.tbl_type_ex) ||
    isOffTableExerciseType(ex.capture_type_ex) ||
    isOffTableExerciseType(ex.exercise?.type_ex) ||
    isOffTableExerciseType(ex.exercise?.tbl_type_ex) ||
    isOffTableExerciseType(ex.tbl_exercise?.tbl_type_ex)
  );
}

/** Types that list exercises by type_id only (no subtype step in coach add flow). */
export function isSubtypeFreeExerciseType(typeRow) {
  const en = String(typeRow?.type_name_en || "").trim();
  return (
    en === "Off-table exercises" ||
    isKingOfTableExerciseType(typeRow) ||
    isWarmupExerciseType(typeRow)
  );
}

/**
 * Mirror of Backend `executionTypeFromExerciseTypeName`.
 * @param {string|null|undefined} typeNameEn
 * @returns {TeExecutionType}
 */
export function executionTypeFromExerciseTypeName(typeNameEn) {
  const n = String(typeNameEn || "").trim().toLowerCase();
  if (!n) return TE_EXECUTION_TYPES.ONCE;
  if (
    (n.includes("king") && n.includes("table")) ||
    n.includes("montée de table") ||
    n.includes("montee de table")
  ) {
    return TE_EXECUTION_TYPES.ROUNDS;
  }
  if (n.includes("competenc") || n === "warmup" || n.includes("warm-up")) {
    return TE_EXECUTION_TYPES.SEQUENCES;
  }
  if (isServiceExerciseTypeName(typeNameEn)) return TE_EXECUTION_TYPES.SEQUENCES;
  if (isMultiballExerciseTypeName(typeNameEn)) return TE_EXECUTION_TYPES.SEQUENCES;
  return TE_EXECUTION_TYPES.ONCE;
}

/**
 * @param {object|null|undefined} ex
 * @returns {TeExecutionType|null}
 */
export function resolveTeExecutionType(ex) {
  if (ex?.is_kot_type === true || kotTypeNameMatches(ex)) {
    return TE_EXECUTION_TYPES.ROUNDS;
  }
  const stored = ex?.te_execution_type;
  if (
    stored === TE_EXECUTION_TYPES.SEQUENCES ||
    stored === TE_EXECUTION_TYPES.ONCE ||
    stored === TE_EXECUTION_TYPES.ROUNDS
  ) {
    return stored;
  }
  const seqLen = Array.isArray(ex?.sequences) ? ex.sequences.length : 0;
  if (seqLen > 0) return TE_EXECUTION_TYPES.SEQUENCES;
  return TE_EXECUTION_TYPES.ONCE;
}

/** @param {object|null|undefined} ex */
export function isTeSequences(ex) {
  return resolveTeExecutionType(ex) === TE_EXECUTION_TYPES.SEQUENCES;
}

/** @param {object|null|undefined} ex */
export function isTeOnce(ex) {
  return resolveTeExecutionType(ex) === TE_EXECUTION_TYPES.ONCE;
}

/** @param {object|null|undefined} ex */
export function isTeRounds(ex) {
  return resolveTeExecutionType(ex) === TE_EXECUTION_TYPES.ROUNDS;
}

/** Lower-cased catalog type names found on any live / planning exercise row shape. */
export function exerciseTypeNameCandidates(ex) {
  if (!ex || typeof ex !== "object") return [];
  return [
    ex.type_ex?.type_name_en,
    ex.type_ex?.type_name_fr,
    ex.type_details?.type_name_en,
    ex.type_details?.type_name_fr,
    ex.type_name_en,
    ex.type_name_fr,
    ex.tbl_type_ex?.type_name_en,
    ex.tbl_type_ex?.type_name_fr,
    ex.capture_type_ex?.type_name_en,
    ex.capture_type_ex?.type_name_fr,
    ex.tbl_exercise?.tbl_type_ex?.type_name_en,
    ex.tbl_exercise?.tbl_type_ex?.type_name_fr,
    ex.exercise?.type_ex?.type_name_en,
    ex.exercise?.type_ex?.type_name_fr,
    ex.exercise?.tbl_type_ex?.type_name_en,
    ex.exercise?.tbl_type_ex?.type_name_fr,
    ex.sub_type_ex_direct?.tbl_type_ex?.type_name_en,
  ]
    .filter(Boolean)
    .map((s) => String(s).toLowerCase());
}

/**
 * Competency-block training exercise (Blocks of competency / CB sequences).
 */
export function isCompetencyBlockExercise(ex) {
  if (!ex || typeof ex !== "object") return false;
  const typeCandidates = exerciseTypeNameCandidates(ex);
  if (typeCandidates.some((s) => s.includes("competenc"))) return true;
  if (
    ex.competency_block?.cb_id ||
    ex.cb_details?.cb_id ||
    ex.cb_id
  ) {
    return true;
  }
  const seqs = Array.isArray(ex.sequences) ? ex.sequences : [];
  return seqs.some(
    (s) =>
      s?.competency_block?.cb_id ||
      s?.blocks_compentency?.cb_id ||
      s?.cb_id
  );
}

/** Personalized slot: each player runs their own current-cycle drill. */
export function isPersonalizedExercise(ex) {
  if (!ex || typeof ex !== "object") return false;
  if (ex.personalized_slot === true) return true;
  return exerciseTypeNameCandidates(ex).some(
    (s) => s === "personalized" || s === "personnalisé" || s === "personnalise"
  );
}

/**
 * Player performance auto-evaluation applies to competency blocks and
 * personalized exercises. King of the Table is match play, not an autoeval.
 */
export function supportsPlayerPerformanceAutoeval(ex) {
  if (!ex || isKotExercise(ex)) return false;
  return isCompetencyBlockExercise(ex) || isPersonalizedExercise(ex);
}

/** Prompt live autoeval when a competency block or personalized exercise finalizes. */
export function shouldPromptLiveAutoevalOnFinalize(ex) {
  return supportsPlayerPerformanceAutoeval(ex);
}

/** @param {TeExecutionType|null|undefined} mode */
export function teExecutionTypeLabel(mode, t) {
  if (!mode) return "";
  if (mode === TE_EXECUTION_TYPES.SEQUENCES) {
    return t?.("te_mode_sequences");
  }
  if (mode === TE_EXECUTION_TYPES.ONCE) {
    return t?.("te_mode_once");
  }
  if (mode === TE_EXECUTION_TYPES.ROUNDS) {
    return t?.("te_mode_rounds");
  }
  return mode;
}

/**
 * Is this exercise a King of the Table type?
 * Prefers backend `is_kot_type` / `TE_ROUNDS`, with English name heuristic for legacy rows.
 *
 * @param {object|null|undefined} ex
 */
export function isKotExercise(ex) {
  if (!ex) return false;
  return (
    ex.is_kot_type === true ||
    isTeRounds(ex) ||
    kotTypeNameMatches(ex) ||
    isKingOfTableExerciseType(ex.type_ex) ||
    isKingOfTableExerciseType(ex.tbl_type_ex) ||
    isKingOfTableExerciseType(ex.capture_type_ex)
  );
}

/** KoT panel: alias for {@link isKotExercise}. */
export function shouldShowKingOfTablePanel(ex) {
  return isKotExercise(ex);
}
